import React, { useState, useEffect } from "react";
import { Modal, VbButton, Textarea, Checkbox } from "@/components/vb";
import {useAuth} from "@/context/AuthContext.tsx";

interface IssueDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const IssueDialog = ({ open, setOpen }: IssueDialogProps) => {
    const {user} = useAuth();
    const [description, setDescription] = useState("");
    const [followUp, setFollowUp] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitIssueToCustomerIO = async (data: { description: string; followUp: boolean }) => {
        const SITE_ID = import.meta.env.VITE_CUSTOMERIO_SITE_ID;
        const FORM_ID = import.meta.env.VITE_CUSTOMERIO_FORM_ID;
        const success_url = `${window.location.origin}/`;
        const BASE_ACTION = `https://customerioforms.com/forms/submit_action?site_id=${SITE_ID}&form_id=${FORM_ID}&success_url=${encodeURIComponent(success_url)}`;

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = BASE_ACTION;
        form.target = 'customerio_iframe';
        form.style.display = 'none';

        const iframe = document.createElement('iframe');
        iframe.name = 'customerio_iframe';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        const fields = {
            'issue_description': data.description,
            'followup_consent': data.followUp.toString(),
            'feedback_type': 'Report Bug',
            'source_url': window.location.href,
            'submission_timestamp': new Date().toISOString(),
            'email': user?.email ?? ''
        };

        Object.entries(fields).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();

        setTimeout(() => {
            document.body.removeChild(form);
            document.body.removeChild(iframe);
        }, 1000);

        return { success: true };
    };

    const handleSubmit = async () => {
        if (!description.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            await submitIssueToCustomerIO({
                description: description.trim(),
                followUp,
            });
            setIsSuccess(true);
        } catch (error) {
            console.error("Failed to submit issue:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (autoCloseTimer) {
            clearTimeout(autoCloseTimer);
            setAutoCloseTimer(null);
        }
        setDescription("");
        setFollowUp(false);
        setIsSuccess(false);
        setOpen(false);
    };

    useEffect(() => {
        if (isSuccess) {
            const timer = setTimeout(() => {
                handleClose();
            }, 10000);

            setAutoCloseTimer(timer);

            return () => {
                clearTimeout(timer);
                setAutoCloseTimer(null);
            };
        }
    }, [isSuccess]);

    return (
        <Modal
            open={open}
            onOpenChange={setOpen}
            srTitle="Report an Issue"
            widthClassName="max-w-[600px]"
            showCloseButton={false}
            footer={
                <>
                    <VbButton variant="secondary" onClick={handleClose}>
                        Close window
                    </VbButton>
                    {!isSuccess && (
                        <VbButton
                            onClick={handleSubmit}
                            disabled={isSubmitting || !description.trim()}
                        >
                            {isSubmitting ? "Submitting..." : "Submit my response"}
                        </VbButton>
                    )}
                </>
            }
        >
            {isSuccess ? (
                <div className="flex flex-col items-center justify-center gap-4 py-2">
                    <img src="/reportIssue.svg" alt="Success illustration" className="h-56 w-56" />
                    <div className="text-center">
                        <h3 className="text-[15px] font-bold text-vb-text">
                            Thank you for your feedback!
                        </h3>
                        <p className="text-[15px] font-bold text-vb-text">
                            Your response was submitted and we are taking care of it!
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    <h2 className="text-[15px] font-bold text-vb-text">Describe your issue</h2>

                    <div>
                        <label htmlFor="issueDescription" className="mb-2 block text-[13.5px] font-bold text-vb-text">
                            What went wrong or what would you like to report?{" "}
                            <span className="text-vb-fail">*</span>
                        </label>
                        <p className="mb-3 text-[13px] text-vb-text-2">
                            Please describe the issue, unexpected behavior, or feedback you have. The more detail, the better!
                        </p>
                        <Textarea
                            id="issueDescription"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Message...."
                            rows={5}
                        />
                    </div>

                    <div>
                        <h3 className="mb-2 text-[13.5px] font-bold text-vb-text">
                            Would you like us to contact you for a follow-up feedback session?
                        </h3>
                        <p className="mb-3 text-[13px] text-vb-text-2">
                            If you'd like to discuss your feedback in more detail, select the box and we may reach out to schedule a quick call. Totally optional - but we'd love to hear more if you're open to it.
                        </p>
                        <Checkbox
                            id="followUp"
                            checked={followUp}
                            onChange={(e) => setFollowUp(e.target.checked)}
                        >
                            Yes, I'm open to talk more and share my feedback
                        </Checkbox>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default IssueDialog;
